import { Building2, Package, ShoppingCart, BarChart3, FileText } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { VenueSelection, LongTermTenderVenue } from '../../types/map';

interface ReviewStepProps {
  venueSelections: VenueSelection[];
  skuSelections: { machineId: string; row: number; col: number }[];
  retailMediaSelections: Record<string, any>;
  reportingTier: 'basic' | 'professional' | 'enterprise' | undefined;
  reportingOptions: {
    liveReporting?: boolean;
    machineBracket?: string;
    skuTier?: string;
  };
  notes: string;
  onNotesChange: (notes: string) => void;
  venues: LongTermTenderVenue[];
}

export function ReviewStep({
  venueSelections,
  skuSelections,
  retailMediaSelections,
  reportingTier,
  reportingOptions,
  notes,
  onNotesChange,
  venues,
}: ReviewStepProps) {
  const totalMachines = venueSelections.reduce((sum, vs) => sum + vs.machineCount, 0);
  const totalSKUs = skuSelections.length;
  
  // Calculate unique SKU positions (row, col combinations) for display
  const uniqueSKUPositions = new Set(
    skuSelections.map(s => `${s.row}-${s.col}`)
  ).size;

  const formatRetailMediaName = (itemId: string): string => {
    const nameMap: Record<string, string> = {
      'dooh-ad': 'Digital Screen Ad',
      'home-banner': 'Home Screen Banner',
      'category-icon-lead': 'Category Icon Lead',
      'category-banner': 'Category Banner',
      'premium-listing': 'Premium Product Placement',
      'exclusive-category': 'Exclusive Category',
      'enhanced-detail': 'Enhanced Product Page',
      'vend-video': 'Vend Video Ad',
      'virtual-sku': 'Virtual SKU Listing',
      'bundle-sku': 'Bundle SKU Listing',
      'subscription-sku': 'Subscription SKU Listing',
      'safe-qr': 'SafeQR Redirect',
      'customer-survey': 'Customer Survey',
      'customer-signup': 'Customer Sign-Up',
    };
    return nameMap[itemId] || itemId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Extract time period
  const timePeriod = retailMediaSelections._timePeriod || 8;
  
  // Filter out empty retail media selections
  const activeRetailMedia = Object.entries(retailMediaSelections).filter(([key, data]: [string, any]) => {
    if (key === '_timePeriod') return false;
    const machines = typeof data.machines === 'number' ? data.machines : 0;
    const units = typeof data.units === 'number' ? data.units : 0;
    return machines > 0 && units > 0;
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tender Request Summary</h2>
        <p className="text-gray-600">
          Review your selections before submitting your long-term tender request.
        </p>
      </div>

      {/* Main Quote Container */}
      <Card className="shadow-lg">
        <CardContent className="p-8">
          {/* Venue & Machine Selections Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-600">
              <Building2 className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Venue & Machine Selections</h3>
            </div>
            <div className="space-y-3">
              {venueSelections.map((selection) => {
                return (
                  <div key={selection.venueName} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div className="font-semibold text-gray-900">{selection.venueName}</div>
                    <div className="text-sm font-medium text-gray-700">
                      {selection.machineCount} machine{selection.machineCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SKU Listings Section */}
          {totalSKUs > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-600">
                <Package className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">SKU Listings</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2">
                  <div className="font-semibold text-gray-900">SKU Positions Selected</div>
                  <div className="text-sm font-medium text-gray-700">
                    {uniqueSKUPositions} SKU{uniqueSKUPositions !== 1 ? 's' : ''} across {totalMachines} machine{totalMachines !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Retail Media Selections Section */}
          {activeRetailMedia.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-600">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Retail Media Selections</h3>
                <span className="text-sm font-medium text-gray-600">({timePeriod} weeks)</span>
              </div>
              <div className="space-y-3">
                {activeRetailMedia.map(([itemId, data]: [string, any]) => {
                  const machines = typeof data.machines === 'number' ? data.machines : 0;
                  const units = typeof data.units === 'number' ? data.units : 0;
                  return (
                    <div key={itemId} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div className="font-semibold text-gray-900">{formatRetailMediaName(itemId)}</div>
                      <div className="text-sm font-medium text-gray-700">
                        {machines} machine{machines !== 1 ? 's' : ''} × {units} unit{units !== 1 ? 's' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reporting & Analytics Section */}
          {reportingTier && (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-600">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Reporting & Analytics</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Reporting Package</span>
                  <span className="text-gray-700 capitalize">{reportingTier}</span>
                </div>
              </div>
            </div>
          )}

          {/* Additional Notes Section */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-600">
              <FileText className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Additional Notes</h3>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Note:</span> Please mention here if you need more SKUs than available or longer-term retail media listings.
              </p>
            </div>
            <div>
              <Label htmlFor="notes" className="sr-only">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Enter any additional notes or requirements... (e.g., requesting more SKUs than available, longer-term retail media listings, etc.)"
                rows={4}
                className="w-full"
              />
            </div>
          </div>

          {/* Summary Footer */}
          <div className="mt-8 pt-6 border-t-2 border-gray-300 bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">Request Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{venueSelections.length}</div>
                <div className="text-sm text-gray-600 mt-1">Venue{venueSelections.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{totalMachines}</div>
                <div className="text-sm text-gray-600 mt-1">Machine{totalMachines !== 1 ? 's' : ''}</div>
              </div>
              {totalSKUs > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{uniqueSKUPositions}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Facing{uniqueSKUPositions !== 1 ? 's' : ''}</div>
                </div>
              )}
              {activeRetailMedia.length > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{activeRetailMedia.length}</div>
                  <div className="text-sm text-gray-600 mt-1">Retail Media Item{activeRetailMedia.length !== 1 ? 's' : ''}</div>
                </div>
              )}
            </div>
            {reportingTier && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                <div className="text-sm text-gray-600">Reporting Package</div>
                <div className="text-lg font-semibold text-gray-900 capitalize mt-1">{reportingTier}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
